import { useState } from "react";

import { AccordionKeys } from "@shared";

import { DocumentGroup, documentsApi } from "@/api";
import { BulkActionBar } from "@/common/components/layout/components/dropzone/components/bulk-action-bar/BulkActionBar";
import { MergeFilesDialog } from "@/common/components/layout/components/dropzone/components/merge-files-dialog/MergeFilesDialog";
import { useMergeFiles } from "@/common/components/layout/components/dropzone/components/merge-files-dialog/hooks/useMergeFiles";
import { useClientId } from "@/common/hooks/useClientId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { DropzoneAccordion } from "../dropzone-accordion/DropzoneAccordion";
import { DocumentList } from "../dropzone-section/components/document-list/DocumentList";
import { UploadLinkControl } from "./components/upload-link-control/UploadLinkControl";
import { useClientSelfUploads } from "./hooks/useClientSelfUploads";

export function ClientSelfUploads() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const {
    files,
    pendingIncomingMoves,
    selectedFiles,
    handleDelete,
    handleToggleSelect,
    fileActions,
    handleBulkDownload,
    isBulkActionPending,
  } = useClientSelfUploads(clientId);

  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const selectedFileList = files.filter((f) => selectedFiles.has(f.documentId));

  const mergeMutation = useMergeFiles({
    mergeFn: (orderedItems) => {
      if (!clientId) {
        throw new Error("clientId is required");
      }
      return documentsApi.mergeDocuments({
        clientId,
        mergeDocumentsRequest: {
          items: orderedItems.map((it) => ({ documentId: it.fileId, rotationDegrees: it.rotationDegrees })),
        },
      });
    },
    invalidationKeys: [[QueryKey.ClientSelfDocuments, clientId]],
    onSuccess: () => setIsMergeOpen(false),
  });

  if (!clientId) {
    return null;
  }

  return (
    <DropzoneAccordion
      title={t("dropzone.panel.uploadsFromClient", { count: files.length })}
      zoneId={DocumentGroup.ClientSelf}
      accordionKey={AccordionKeys.dropzone.clientSelfUploads}
    >
      <DocumentList
        files={files}
        uploadingFiles={[]}
        pendingIncomingMoves={pendingIncomingMoves}
        selectedFiles={selectedFiles}
        onToggleSelect={handleToggleSelect}
        fileActions={fileActions}
        sourceZone={DocumentGroup.ClientSelf}
      />
      <BulkActionBar
        selectedCount={selectedFiles.size}
        onDownload={handleBulkDownload}
        onDelete={() => handleDelete(selectedFileList)}
        onMerge={() => setIsMergeOpen(true)}
        disabled={isBulkActionPending}
      />
      <UploadLinkControl clientId={clientId} />
      {isMergeOpen && selectedFileList.length >= 2 && (
        <MergeFilesDialog
          files={selectedFileList}
          onCancel={() => setIsMergeOpen(false)}
          onConfirm={(orderedItems) => mergeMutation.mutate(orderedItems)}
          isSubmitting={mergeMutation.isPending}
          fetchImageBlob={fileActions.onFetchDownloadBlob}
        />
      )}
    </DropzoneAccordion>
  );
}
